/**
 * The window frame: a title bar, a rail of boards, and one scrolling pane between them.
 *
 * This is an application, not a document. The frame is the height of the viewport and never
 * scrolls; what scrolls is the pane, so the rail and the title bar stay where they were put
 * and a board can pin a toolbar to its own top or a transport to its own bottom without
 * fighting the page for it. That is the arrangement every notation program uses, and the
 * boards below were rebuilt to sit in it.
 *
 * Every board is handed the bare pane and paints to its own edges: the two tools paint a
 * desk, and every other board paints a strip and a document with `Board`. A margin dealt out
 * by the frame would put one board's padding around another board's toolbar, and a scroller
 * owned by the frame would scroll a toolbar away that its board had pinned.
 */

import { Outlet, useLocation } from "react-router-dom";
import TabBar, { boardAt } from "./TabBar";

export default function AppShell(): JSX.Element {
  const board = boardAt(useLocation().pathname)?.label ?? "";

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-shell-deep">
      <header className="flex h-10 shrink-0 items-center gap-3 border-b border-shell-edge bg-shell px-3 text-shell-text">
        <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-shell-dim">
          <svg viewBox="0 0 20 20" className="h-4 w-4 text-shell-live" aria-hidden="true">
            <path
              d="M4 15.5a2 2 0 1 0 4 0 2 2 0 1 0-4 0 M8 15.5V4l8-1.5V13 M12 13a2 2 0 1 0 4 0 2 2 0 1 0-4 0"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          </svg>
          Arranger AI
        </span>
        {board === "" ? null : (
          <>
            <span aria-hidden="true" className="h-4 w-px bg-shell-seam" />
            <span className="text-[13px] text-shell-lit">{board}</span>
          </>
        )}
      </header>

      <div className="flex min-h-0 flex-1 flex-col-reverse md:flex-row">
        <TabBar />
        {/* `min-h-0` as well as `min-w-0`: on a phone the rail is a bar and this is an item
            in a column, where a flex item's automatic minimum is its content. Without it the
            pane grows to whatever a board holds and pushes its own top off the screen, which
            is how the Lab board lost the first of its two desks entirely. */}
        <main className="flex min-h-0 min-w-0 flex-1 flex-col">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
